import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SUPPORTED_LANGUAGES, MUSIC_GENRES, WELLNESS_GOALS, getTranslation } from '../translations';
import { Check, User, Phone, MapPin, Globe } from 'lucide-react';
import { toast } from 'sonner';

const COUNTRIES = [
  { code: 'US', abbr: 'USA', name: 'United States', phoneCode: '+1', flag: '🇺🇸' },
  { code: 'GB', abbr: 'UK', name: 'United Kingdom', phoneCode: '+44', flag: '🇬🇧' },
  { code: 'IN', abbr: 'IND', name: 'India', phoneCode: '+91', flag: '🇮🇳' },
  { code: 'AE', abbr: 'UAE', name: 'United Arab Emirates', phoneCode: '+971', flag: '🇦🇪' },
  { code: 'SA', abbr: 'KSA', name: 'Saudi Arabia', phoneCode: '+966', flag: '🇸🇦' },
  { code: 'CA', abbr: 'CAN', name: 'Canada', phoneCode: '+1', flag: '🇨🇦' },
  { code: 'AU', abbr: 'AUS', name: 'Australia', phoneCode: '+61', flag: '🇦🇺' },
  { code: 'DE', abbr: 'GER', name: 'Germany', phoneCode: '+49', flag: '🇩🇪' },
  { code: 'FR', abbr: 'FRA', name: 'France', phoneCode: '+33', flag: '🇫🇷' },
  { code: 'JP', abbr: 'JPN', name: 'Japan', phoneCode: '+81', flag: '🇯🇵' },
  { code: 'CN', abbr: 'CHN', name: 'China', phoneCode: '+86', flag: '🇨🇳' },
  { code: 'BR', abbr: 'BRA', name: 'Brazil', phoneCode: '+55', flag: '🇧🇷' },
  { code: 'MX', abbr: 'MEX', name: 'Mexico', phoneCode: '+52', flag: '🇲🇽' },
  { code: 'IT', abbr: 'ITA', name: 'Italy', phoneCode: '+39', flag: '🇮🇹' },
  { code: 'ES', abbr: 'ESP', name: 'Spain', phoneCode: '+34', flag: '🇪🇸' },
  { code: 'KR', abbr: 'KOR', name: 'South Korea', phoneCode: '+82', flag: '🇰🇷' },
  { code: 'SG', abbr: 'SGP', name: 'Singapore', phoneCode: '+65', flag: '🇸🇬' },
  { code: 'PH', abbr: 'PHL', name: 'Philippines', phoneCode: '+63', flag: '🇵🇭' },
  { code: 'PK', abbr: 'PAK', name: 'Pakistan', phoneCode: '+92', flag: '🇵🇰' },
  { code: 'NG', abbr: 'NGA', name: 'Nigeria', phoneCode: '+234', flag: '🇳🇬' },
  { code: 'EG', abbr: 'EGY', name: 'Egypt', phoneCode: '+20', flag: '🇪🇬' },
  { code: 'ZA', abbr: 'RSA', name: 'South Africa', phoneCode: '+27', flag: '🇿🇦' },
  { code: 'TH', abbr: 'THA', name: 'Thailand', phoneCode: '+66', flag: '🇹🇭' },
  { code: 'MY', abbr: 'MYS', name: 'Malaysia', phoneCode: '+60', flag: '🇲🇾' },
  { code: 'ID', abbr: 'IDN', name: 'Indonesia', phoneCode: '+62', flag: '🇮🇩' },
  { code: 'TR', abbr: 'TUR', name: 'Turkey', phoneCode: '+90', flag: '🇹🇷' },
  { code: 'NL', abbr: 'NED', name: 'Netherlands', phoneCode: '+31', flag: '🇳🇱' },
  { code: 'SE', abbr: 'SWE', name: 'Sweden', phoneCode: '+46', flag: '🇸🇪' },
  { code: 'CH', abbr: 'SUI', name: 'Switzerland', phoneCode: '+41', flag: '🇨🇭' },
  { code: 'NZ', abbr: 'NZL', name: 'New Zealand', phoneCode: '+64', flag: '🇳🇿' }
];

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const googleUser = location.state?.user;
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    surname: '',
    gender: '',
    country: '',
    area: '',
    phone: '',
    country_code: '+1',
    selected_country_code: 'US',
    language: 'en',
    music_preferences: [],
    wellness_goals: []
  });

  const t = (key) => getTranslation(key, preferences.language);

  const toggleSelection = (category, value) => {
    setPreferences(prev => {
      const current = prev[category];
      const newValues = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [category]: newValues };
    });
  };

  const handleCountryChange = (countryCode) => {
    const country = COUNTRIES.find(c => c.code === countryCode);
    if (country) {
      setPreferences({
        ...preferences,
        country: country.name,
        country_code: country.phoneCode
      });
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/auth/complete-profile`, {
        surname: preferences.surname,
        gender: preferences.gender,
        country: preferences.country,
        area: preferences.area,
        phone: preferences.phone ? `${preferences.country_code}${preferences.phone}` : null,
        language: preferences.language,
        music_preferences: preferences.music_preferences,
        wellness_goals: preferences.wellness_goals
      });
      
      // Refresh user data to get updated language preference
      const userRes = await axios.get(`${API}/auth/me`);
      
      toast.success('Profile completed!');
      
      // Force page reload to apply new language preference
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  const totalSteps = 5;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-stone-50 to-stone-100 flex items-center justify-center p-4 md:p-6">
      <div className="zen-card p-6 md:p-10 max-w-2xl w-full">
        <div className="mb-8">
          <h2 className="text-3xl md:text-4xl font-semibold mb-3 text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
            Welcome{googleUser?.name ? `, ${googleUser.name.split(' ')[0]}` : ''}!
          </h2>
          <p className="text-warm-charcoal/60">Let's personalize your experience</p>
          <div className="flex gap-2 mt-6">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                  s <= step ? 'bg-hunter-green' : 'bg-stone-200'
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-warm-charcoal/60 mt-2">Step {step} of {totalSteps}</p>
        </div>

        {/* Step 1: Surname & Gender */}
        {step === 1 && (
          <div className="space-y-6 fade-in">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-warm-charcoal/80 mb-2 block">
                  Surname (Last Name)
                </Label>
                <Input
                  type="text"
                  value={preferences.surname}
                  onChange={(e) => setPreferences({ ...preferences, surname: e.target.value })}
                  placeholder="Enter your surname"
                  className="w-full"
                />
              </div>
              
              <Label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider mt-6 block">
                Select Your Gender
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setPreferences({ ...preferences, gender: 'male' })}
                  className={`p-6 rounded-2xl border-2 transition-all duration-500 ${
                    preferences.gender === 'male'
                      ? 'border-hunter-green bg-hunter-green/10 shadow-lg scale-105'
                      : 'border-stone-200 hover:border-hunter-green/50'
                  }`}
                >
                  <div className="w-16 h-16 rounded-full bg-blue-100 mx-auto mb-3 flex items-center justify-center">
                    <User className="w-8 h-8 text-blue-600" />
                  </div>
                  <p className="font-semibold">Male</p>
                </button>
                <button
                  onClick={() => setPreferences({ ...preferences, gender: 'female' })}
                  className={`p-6 rounded-2xl border-2 transition-all duration-500 ${
                    preferences.gender === 'female'
                      ? 'border-hunter-green bg-hunter-green/10 shadow-lg scale-105'
                      : 'border-stone-200 hover:border-hunter-green/50'
                  }`}
                >
                  <div className="w-16 h-16 rounded-full bg-pink-100 mx-auto mb-3 flex items-center justify-center">
                    <User className="w-8 h-8 text-pink-600" />
                  </div>
                  <p className="font-semibold">Female</p>
                </button>
              </div>
            </div>
            <Button
              onClick={() => setStep(2)}
              disabled={!preferences.gender}
              className="w-full zen-button bg-hunter-green text-white disabled:opacity-50 mt-6"
            >
              Continue
            </Button>
          </div>
        )}

        {/* Step 2: Location */}
        {step === 2 && (
          <div className="space-y-6 fade-in">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-6 h-6 text-hunter-green" />
              <Label className="text-lg font-semibold">Your Location</Label>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-warm-charcoal/80 mb-2 block">
                  Country
                </Label>
                <select
                  value={COUNTRIES.find(c => c.name === preferences.country)?.code || ''}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white focus:border-hunter-green/50 focus:ring-4 focus:ring-hunter-green/10 text-base"
                >
                  <option value="">Select your country</option>
                  {COUNTRIES.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-sm font-medium text-warm-charcoal/80 mb-2 block">
                  Area / City
                </Label>
                <Input
                  type="text"
                  value={preferences.area}
                  onChange={(e) => setPreferences({ ...preferences, area: e.target.value })}
                  placeholder="e.g., Downtown Dubai, Manhattan, Central London"
                  className="w-full"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setStep(3)}
                disabled={!preferences.country}
                className="flex-1 zen-button bg-hunter-green text-white disabled:opacity-50"
              >
                Continue
              </Button>
              <button
                onClick={() => setStep(3)}
                className="px-6 text-sm text-warm-charcoal/60 hover:underline"
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Phone Number */}
        {step === 3 && (
          <div className="space-y-6 fade-in">
            <div className="flex items-center gap-3 mb-4">
              <Phone className="w-6 h-6 text-hunter-green" />
              <Label className="text-lg font-semibold">Phone Number (Optional)</Label>
            </div>
            
            <div className="flex gap-3">
              <div className="w-36">
                <Label className="text-xs font-medium text-warm-charcoal/70 mb-2 block">
                  Country
                </Label>
                <select
                  value={preferences.selected_country_code || 'US'}
                  onChange={(e) => {
                    const country = COUNTRIES.find(c => c.code === e.target.value);
                    if (country) {
                      setPreferences({ 
                        ...preferences, 
                        country_code: country.phoneCode,
                        selected_country_code: country.code
                      });
                    }
                  }}
                  className="w-full px-3 py-3 rounded-xl border border-stone-200 bg-white text-sm font-medium focus:border-hunter-green/50 focus:ring-2 focus:ring-hunter-green/10"
                >
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.abbr} ({c.phoneCode})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <Label className="text-xs font-medium text-warm-charcoal/70 mb-2 block">
                  Phone Number
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-warm-charcoal/50 font-medium">
                    {preferences.country_code}
                  </span>
                  <Input
                    type="tel"
                    value={preferences.phone}
                    onChange={(e) => setPreferences({ ...preferences, phone: e.target.value.replace(/\D/g, '') })}
                    placeholder="1234567890"
                    className="w-full pl-16"
                  />
                </div>
              </div>
            </div>
            
            <p className="text-sm text-warm-charcoal/50">
              Your phone number helps us send you important notifications and connect you with local wellness events.
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setStep(4)}
                className="flex-1 zen-button bg-hunter-green text-white"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Language & Music */}
        {step === 4 && (
          <div className="space-y-6 fade-in">
            <div>
              <Label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider mb-4 block">
                Preferred Language
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {SUPPORTED_LANGUAGES.slice(0, 6).map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setPreferences({ ...preferences, language: lang.code })}
                    className={`p-3 rounded-xl border-2 transition-all duration-500 ${
                      preferences.language === lang.code
                        ? 'border-hunter-green bg-hunter-green/5'
                        : 'border-stone-200 hover:border-hunter-green/30'
                    }`}
                  >
                    <p className="font-semibold text-sm">{lang.nativeName}</p>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="mt-6">
              <Label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider mb-4 block">
                Music Preferences
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {MUSIC_GENRES.map((genre) => (
                  <button
                    key={genre.value}
                    onClick={() => toggleSelection('music_preferences', genre.value)}
                    className={`p-3 rounded-xl border-2 transition-all duration-500 flex items-center gap-2 ${
                      preferences.music_preferences.includes(genre.value)
                        ? 'border-hunter-green bg-hunter-green/5'
                        : 'border-stone-200 hover:border-hunter-green/30'
                    }`}
                  >
                    {preferences.music_preferences.includes(genre.value) && (
                      <Check className="w-4 h-4 text-hunter-green" />
                    )}
                    <p className="font-medium text-sm">{genre.label}</p>
                  </button>
                ))}
              </div>
            </div>
            
            <Button onClick={() => setStep(5)} className="w-full zen-button bg-hunter-green text-white mt-6">
              Continue
            </Button>
          </div>
        )}

        {/* Step 5: Wellness Goals */}
        {step === 5 && (
          <div className="space-y-6 fade-in">
            <Label className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider mb-4 block">
              What brings you to MyMojo?
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {WELLNESS_GOALS.map((goal) => (
                <button
                  key={goal.value}
                  onClick={() => toggleSelection('wellness_goals', goal.value)}
                  className={`p-4 rounded-xl border-2 transition-all duration-500 text-left ${
                    preferences.wellness_goals.includes(goal.value)
                      ? 'border-hunter-green bg-hunter-green/5'
                      : 'border-stone-200 hover:border-hunter-green/30'
                  }`}
                >
                  {preferences.wellness_goals.includes(goal.value) && (
                    <Check className="w-4 h-4 text-hunter-green mb-1" />
                  )}
                  <p className="font-semibold text-sm">{goal.label}</p>
                </button>
              ))}
            </div>
            <Button
              onClick={handleComplete}
              disabled={loading}
              className="w-full zen-button bg-hunter-green text-white mt-6"
            >
              {loading ? 'Saving...' : 'Complete Setup'}
            </Button>
          </div>
        )}

        <button
          onClick={handleSkip}
          className="w-full text-sm text-warm-charcoal/50 hover:text-warm-charcoal mt-4"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
